import React from 'react';
import { observer } from 'mobx-react';
import { observable, action } from 'mobx';
import moment from 'moment';
import styled from 'styled-components';
import { Project } from '../../store/Project';
import { Company, CompanyStore } from '../../store/Company';
import { Team, TeamStore } from '../../store/Team';
import { User, UserStore } from '../../store/User';
import { DeveloperAllocation } from '../../store/planning';
import { Milestone, MilestoneStore } from '../../store/planning/Milestone';
import { api } from '../../store/Base';

const C = {
    bg: '#ffffff', border: '#e0e3e8',
    textPrimary: '#1a1d23', textSecondary: '#5c6470', textMuted: '#9099a8',
    accent: '#2185d0', accentLight: '#e8f3fb',
    success: '#21ba45', successLight: '#eafbef',
    warning: '#f0a721', warningLight: '#fef5e7',
    danger: '#db2828', weekend: '#f0f2f5',
    sidebarBg: '#f8f9fa', inputBg: '#f8f9fa',
};

const PALETTE = ['#e53e3e', '#38a169', '#3182ce', '#805ad5', '#dd6b20', '#319795', '#d53f8c', '#6b46c1'];
function colorFor(s: string): string { let h = 0; for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0; return PALETTE[Math.abs(h) % PALETTE.length]; }

const STATUS_COLORS: Record<string, string> = { planning: '#805ad5', active: '#3182ce', completed: '#38a169', on_hold: '#dd6b20' };
const STATUS_LABELS: Record<string, string> = { planning: 'Planning', active: 'Active', completed: 'Completed', on_hold: 'On Hold' };
const DAY_SHORT: Record<number, string> = { 0: 'Mon', 1: 'Tue', 2: 'Wed', 3: 'Thu', 4: 'Fri', 5: 'Sat', 6: 'Sun' };

const Panel = styled.div<{ visible: boolean }>`
    position: absolute; top: 0; right: 0; bottom: 0; width: 400px;
    background: ${C.bg}; box-shadow: -4px 0 20px rgba(0,0,0,0.12);
    z-index: 50; display: flex; flex-direction: column;
    transition: transform 0.25s ease;
    transform: ${props => props.visible ? 'translateX(0)' : 'translateX(100%)'};
    border-left: 1px solid ${C.border};
`;
const PanelHeader = styled.div` display: flex; justify-content: space-between; align-items: center; padding: 12px 16px; border-bottom: 1px solid ${C.border}; background: ${C.sidebarBg}; flex-shrink: 0; `;
const PanelTitle = styled.h3` font-size: 14px; font-weight: 600; color: ${C.textPrimary}; margin: 0; flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; `;
const CloseBtn = styled.button` background: none; border: none; font-size: 20px; color: ${C.textMuted}; cursor: pointer; padding: 4px 8px; &:hover { color: ${C.textPrimary}; } `;
const PanelContent = styled.div` flex: 1; overflow-y: auto; padding: 16px; `;
const Section = styled.div` margin-bottom: 20px; `;
const SectionLabel = styled.label` display: block; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; color: ${C.textSecondary}; margin-bottom: 6px; `;
const TextInput = styled.input` width: 100%; padding: 8px 10px; background: ${C.inputBg}; border: 1px solid ${C.border}; border-radius: 4px; color: ${C.textPrimary}; font-size: 13px; font-family: inherit; &:focus { outline: none; border-color: ${C.accent}; background: #fff; } `;
const SelectInput = styled.select` width: 100%; padding: 8px 10px; background: ${C.inputBg}; border: 1px solid ${C.border}; border-radius: 4px; color: ${C.textPrimary}; font-size: 13px; font-family: inherit; &:focus { outline: none; border-color: ${C.accent}; background: #fff; } `;
const ToggleRow = styled.div` display: flex; align-items: center; gap: 8px; `;
const ToggleLabel = styled.span` font-size: 13px; color: ${C.textPrimary}; `;
const Toggle = styled.input` cursor: pointer; width: 16px; height: 16px; `;
const DeleteBtn = styled.button` width: 100%; padding: 8px; border: 1px solid ${C.danger}; border-radius: 4px; background: #fff; color: ${C.danger}; cursor: pointer; font-size: 13px; font-weight: 500; &:hover { background: ${C.danger}; color: #fff; } `;
const SaveIndicator = styled.span<{ visible: boolean }>` font-size: 11px; color: ${C.success}; opacity: ${props => props.visible ? 1 : 0}; transition: opacity 0.3s; `;

const MemberCard = styled.div` display: flex; align-items: center; gap: 8px; padding: 8px; border: 1px solid ${C.border}; border-radius: 6px; margin-bottom: 6px; background: ${C.sidebarBg}; `;
const MemberAvatar = styled.span` width: 28px; height: 28px; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: #fff; font-size: 11px; font-weight: 600; flex-shrink: 0; `;
const MemberInfo = styled.div` flex: 1; min-width: 0; `;
const MemberName = styled.div` font-size: 13px; font-weight: 600; color: ${C.textPrimary}; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; `;
const MemberAlloc = styled.div` font-size: 11px; color: ${C.textMuted}; `;
const AvailBar = styled.div` height: 6px; border-radius: 3px; background: ${C.border}; overflow: hidden; margin-top: 4px; `;
const availColor = (pct: number): string => {
    if (pct >= 80) return C.success;
    if (pct >= 40) return C.warning;
    return C.danger;
};
const AvailFill = styled.div<{ pct: number }>` height: 100%; width: ${props => props.pct}%; background: ${props => availColor(props.pct)}; border-radius: 3px; transition: width 0.3s; `;

const MilestoneCard = styled.div` padding: 8px 10px; border: 1px solid ${C.border}; border-radius: 6px; margin-bottom: 6px; background: ${C.sidebarBg}; cursor: pointer; &:hover { background: ${C.accentLight}; border-color: ${C.accent}; } `;
const MilestoneTop = styled.div` display: flex; align-items: center; gap: 6px; `;
const MilestoneDot = styled.span<{ color: string }>` width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; background: ${props => props.color}; `;
const MilestoneName = styled.span` font-size: 13px; font-weight: 500; color: ${C.textPrimary}; flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; `;
const MilestoneMeta = styled.span` font-size: 11px; color: ${C.textMuted}; flex-shrink: 0; `;
const MilestoneDeadline = styled.div` font-size: 11px; color: ${C.textSecondary}; margin-top: 2px; padding-left: 14px; `;
const EmptyMsg = styled.div` font-size: 12px; color: ${C.textMuted}; padding: 8px; text-align: center; `;

interface Props {
    project: Project | null;
    visible: boolean;
    allocations: DeveloperAllocation[];
    onClose: () => void;
    onChanged: () => void;
    onMilestoneClick?: (milestoneId: number) => void;
}

@observer
export default class ProjectEditPanel extends React.Component<Props> {
    companyStore = new CompanyStore();
    teamStore = new TeamStore();
    userStore = new UserStore({ relations: ['team'] });
    milestoneStore = new MilestoneStore({ relations: ['project'] });
    @observable justSaved = false;
    saveTimer: any = null;
    patchTimer: any = null;
    pendingFields: Record<string, any> = null;

    componentDidMount() {
        document.addEventListener('keydown', this.handleKeyDown);
        this.companyStore.fetch().catch(() => null);
        this.teamStore.fetch().catch(() => null);
        this.userStore.fetch().catch(() => null);
    }

    componentDidUpdate(prev: Props) {
        if (this.props.project?.id !== prev.project?.id && this.props.project?.id) {
            this.loadProjectData();
        }
    }

    async loadProjectData() {
        const pid = this.props.project?.id;
        if (!pid) return;
        try {
            await this.milestoneStore.fetch({ params: { project: pid } });  // eslint-disable-line
        } catch (e) { /* ignore */ }
    }

    componentWillUnmount() {
        document.removeEventListener('keydown', this.handleKeyDown);
        if (this.saveTimer) clearTimeout(this.saveTimer);
        // Flush a pending debounced patch so no edit is lost on close
        if (this.patchTimer) this.sendPatch();
    }
    @action handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape' && this.props.visible) {
            e.preventDefault();
            this.props.onClose();
        }
    };

    // Debounced: patching on every keystroke races with in-flight requests
    // (an older request can land after a newer one and clobber the value).
    // Flushes on blur/unmount via sendPatch so the final edit always lands.
    @action patch = (fields: Record<string, any>) => {
        if (!this.props.project?.id) return;
        this.pendingFields = fields;
        if (this.patchTimer) clearTimeout(this.patchTimer);
        this.patchTimer = setTimeout(() => this.sendPatch(), 300);
    };

    @action sendPatch = async () => {
        if (this.patchTimer) { clearTimeout(this.patchTimer); this.patchTimer = null; }
        const fields = this.pendingFields;
        this.pendingFields = null;
        if (!fields || !this.props.project?.id) return;
        try {
            await api.patch('project/' + this.props.project.id + '/', fields);
            this.justSaved = true;
            if (this.saveTimer) clearTimeout(this.saveTimer);
            this.saveTimer = setTimeout(() => { this.justSaved = false; }, 1500);
            this.props.onChanged();
        } catch (e: any) { console.error('ProjectEditPanel patch error:', e); }
    };

    @action handleName = (e: React.ChangeEvent<HTMLInputElement>) => { const v = e.target.value; (this.props.project as any).name = v; if (v.trim()) this.patch({ name: v }); };
    @action handleCompany = (e: React.ChangeEvent<HTMLSelectElement>) => { const v = e.target.value ? Number(e.target.value) : null; (this.props.project as any).company = v; this.patch({ company: v }); };
    @action handleTeam = (e: React.ChangeEvent<HTMLSelectElement>) => { const v = e.target.value ? Number(e.target.value) : null; (this.props.project as any).team = v; this.patch({ team: v }); };
    @action handleLead = (e: React.ChangeEvent<HTMLSelectElement>) => { const v = e.target.value ? Number(e.target.value) : null; (this.props.project as any).projectLead = v; this.patch({ project_lead: v }); };
    @action handleHidden = (e: React.ChangeEvent<HTMLInputElement>) => { const v = e.target.checked; (this.props.project as any).hidden = v; this.patch({ hidden: v }); };
    @action handleDelete = async () => {
        if (!this.props.project?.id) return;
        if (!window.confirm('Delete this project?')) return;
        try { await api.delete('project/' + this.props.project.id + '/'); this.props.onClose(); this.props.onChanged(); }
        catch (e: any) { console.error('Delete project error:', e); }
    };

    getProjectAllocations(): DeveloperAllocation[] {
        const pid = this.props.project?.id;
        if (!pid) return [];
        return this.props.allocations.filter(a => {
            const entries = (a as any).entries?.models || (a as any).entries || [];
            return entries.some((e: any) => {
                const epid = e.project?.id ?? e.project;
                return Number(epid) === Number(pid);
            });
        });
    }

    getDevHours(alloc: DeveloperAllocation): { total: number; days: string; pct: number } {
        const entries = (alloc as any).entries?.models || (alloc as any).entries || [];
        const total = entries.reduce((s: number, e: any) => s + (e.hours || 0), 0);
        const daySet = new Set(entries.map((e: any) => e.dayOfWeek));
        const days = Array.from(daySet).sort().map((d: number) => DAY_SHORT[d] || '?').join(', ');
        const pct = Math.min(Math.round((total / 40) * 100), 100);
        return { total, days: days || 'Not allocated', pct };
    }

    get milestones(): Milestone[] {
        const pid = this.props.project?.id;
        if (!pid) return [];
        return (this.milestoneStore.models as Milestone[]).filter((m: any) => {
            const mpid = m.project?.id ?? m.project;
            return Number(mpid) === Number(pid);
        });
    }

    renderMember(alloc: DeveloperAllocation): React.ReactNode {
        const uid = (alloc as any).user?.id ?? (alloc as any).user;
        const user = (this.userStore.models as User[]).find(u => Number(u.id) === Number(uid));
        if (!user) return null;
        const aname = (user as any).fullName || ((user as any).firstName || '') + ' ' + ((user as any).lastName || '').trim();
        const initials = ((user as any).firstName || '?').charAt(0) + ((user as any).lastName || '').charAt(0);
        const { total, days, pct } = this.getDevHours(alloc);
        return (
            <MemberCard key={uid}>
                <MemberAvatar style={{ background: colorFor(aname) }}>{initials}</MemberAvatar>
                <MemberInfo>
                    <MemberName>{aname}</MemberName>
                    <MemberAlloc>{total}h/week · {days}</MemberAlloc>
                    <AvailBar><AvailFill pct={pct} /></AvailBar>
                </MemberInfo>
            </MemberCard>
        );
    }

    renderMilestone(m: Milestone): React.ReactNode {
        const mAny = m as any;
        const color = STATUS_COLORS[mAny.status] || '#805ad5';
        const deadline = mAny.deadline ? moment(mAny.deadline).format('MMM D') : 'No deadline';
        return (
            <MilestoneCard key={m.id} onClick={() => { if (this.props.onMilestoneClick) this.props.onMilestoneClick(m.id); }}>
                <MilestoneTop>
                    <MilestoneDot color={color} />
                    <MilestoneName>{mAny.name}</MilestoneName>
                    <MilestoneMeta>{STATUS_LABELS[mAny.status] || mAny.status}</MilestoneMeta>
                </MilestoneTop>
                <MilestoneDeadline>Due: {deadline}{mAny.startDate ? ' · Started ' + moment(mAny.startDate).format('MMM D') : ''}</MilestoneDeadline>
            </MilestoneCard>
        );
    }

    render() {
        const { project, visible, onClose } = this.props;
        if (!project) return null;
        const p = project as any;
        const companyId = p.company?.id ?? p.company;
        const teamId = p.team?.id ?? p.team;
        const leadId = p.projectLead?.id ?? p.projectLead;
        const allocs = this.getProjectAllocations();
        const milestones = this.milestones;

        return (
            <Panel visible={visible}>
                <PanelHeader>
                    <PanelTitle>{p.name || ('#' + project.id)}</PanelTitle>
                    <SaveIndicator visible={this.justSaved}>✓ Saved</SaveIndicator>
                    <CloseBtn onClick={onClose} data-test-project-panel-close>×</CloseBtn>
                </PanelHeader>
                <PanelContent>
                    <Section>
                        <SectionLabel>Project Name</SectionLabel>
                        <TextInput data-test-project-panel-name value={p.name || ''} onChange={this.handleName} onBlur={this.sendPatch} />
                    </Section>
                    <Section>
                        <SectionLabel>Company</SectionLabel>
                        <SelectInput data-test-project-panel-company value={companyId ?? ''} onChange={this.handleCompany} onBlur={this.sendPatch}>
                            <option value="">—</option>
                            {(this.companyStore.models as Company[]).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </SelectInput>
                    </Section>
                    <Section>
                        <SectionLabel>Team</SectionLabel>
                        <SelectInput data-test-project-panel-team value={teamId ?? ''} onChange={this.handleTeam} onBlur={this.sendPatch}>
                            <option value="">—</option>
                            {(this.teamStore.models as Team[]).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </SelectInput>
                    </Section>
                    <Section>
                        <SectionLabel>Project Lead</SectionLabel>
                        <SelectInput data-test-project-panel-lead value={leadId ?? ''} onChange={this.handleLead} onBlur={this.sendPatch}>
                            <option value="">—</option>
                            {(this.userStore.models as User[]).map(u => {
                                const name = (u as any).fullName || ((u as any).firstName || '') + ' ' + ((u as any).lastName || '').trim() || (u as any).email;
                                return <option key={u.id} value={u.id}>{name}</option>;
                            })}
                        </SelectInput>
                    </Section>
                    <Section>
                        <SectionLabel>Visibility</SectionLabel>
                        <ToggleRow>
                            <Toggle type="checkbox" checked={!!p.hidden} onChange={this.handleHidden} onBlur={this.sendPatch} data-test-project-panel-hidden />
                            <ToggleLabel>Hidden from overview</ToggleLabel>
                        </ToggleRow>
                    </Section>

                    <Section>
                        <SectionLabel>Team and Availability ({allocs.length})</SectionLabel>
                        {allocs.length === 0
                            ? <EmptyMsg>No developers allocated yet</EmptyMsg>
                            : allocs.map(a => this.renderMember(a))}
                    </Section>

                    <Section>
                        <SectionLabel>Milestones ({milestones.length})</SectionLabel>
                        {milestones.length === 0
                            ? <EmptyMsg>No milestones yet</EmptyMsg>
                            : milestones.map(m => this.renderMilestone(m))}
                    </Section>

                    <Section>
                        <DeleteBtn onClick={this.handleDelete} data-test-project-panel-delete>Delete Project</DeleteBtn>
                    </Section>
                </PanelContent>
            </Panel>
        );
    }
}
